import store from '@/core/store'
import {
  setTemporalDraftBufferValues,
  setValueOfActiveDraft,
  setActiveDraft, addDraftListObject,
  removeDraftMessageByMessageId, setDefaultFrom, resetAllFieldsOfDraft,
  setDraftMessageType, setValuesOfActiveDraftOnList, unMarkActiveDraftId,
  unSetTemporalDraftBufferValues
} from '@/core/store/Slices/DraftsSlice'

export function storeDraftId (id) {
  store.dispatch(
    setActiveDraft(id)
  )
}

export function saveTemporalDraftValues (data) {
  store.dispatch(
    setTemporalDraftBufferValues(data)
  )
}

export function getTemporalDraftValues () {
  return store.getState().drafts.temporalDraftValues
}

export function storeTemporalDraftValuesOnActiveDraft () {
  const draftValues = getTemporalDraftValues()
  setActiveDraftValues(draftValues)
  cleanDraftTemporalValues()
}

function cleanDraftTemporalValues () {
  store.dispatch(
    unSetTemporalDraftBufferValues()
  )
}

export function storeDefaultFrom () {
  store.dispatch(
    setDefaultFrom()
  )
}

export function setActiveDraftValues (values) {
  store.dispatch(
    setValuesOfActiveDraftOnList(values)
  )
  sendUpdateDraftEvents()
}

export function unSetActiveDraft () {
  store.dispatch(
    unMarkActiveDraftId()
  )
  cleanDraftTemporalValues()
}

export function resetStandaloneMessage () {
  store.dispatch(
    resetAllFieldsOfDraft()
  )
  cleanDraftTemporalValues()
  unSetActiveDraft()
  switchActiveMessageToEmptyOne()
}

export function switchActiveMessageToEmptyOne () {
  store.commit('DRAFT_MESSAGE:SWITCHED', {})
  sendUpdateDraftEvents()
  sendAttachmentsUpdatedEvent()
}

export function removeDraftMessage (messageId) {
  store.dispatch(
    removeDraftMessageByMessageId(messageId)
  )
  store.commit('DRAFTS_LISTS:UPDATE')
  store.commit('DRAFT_MESSAGE:UPDATE', {})
}

export function addToBoxNameToStore (targetBoxName) {
  setActiveDraftValue('toBox', targetBoxName)
}

export function addToAddressToStore (address) {
  // Spread defensively: getCurrentDraft() can return a draft whose list is
  // absent, and `[...undefined]` throws.
  const tos = [...(getCurrentDraft()?.tos || []), address]
  setActiveDraftValue('tos', tos)
  setActiveDraftValue('tosDirty', true)
}

export function addCCAddressToStore (address) {
  const ccs = [...(getCurrentDraft()?.ccs || []), address]
  setActiveDraftValue('ccs', ccs)
}

export function addBCCAddressToStore (address) {
  const bccs = [...(getCurrentDraft()?.bccs || []), address]
  setActiveDraftValue('bccs', bccs)
}

export function removeToAddressFromDraft (addressValue) {
  const tos = getCurrentDraft().tos.filter((tosAddress) => tosAddress !== addressValue)
  setActiveDraftValue('tos', tos)
  setActiveDraftValue('tosDirty', true)
}
export function removeCCAddressFromDraft (addressValue) {
  const ccs = getCurrentDraft().ccs.filter((tosAddress) => tosAddress !== addressValue)
  setActiveDraftValue('ccs', ccs)
}
export function removeBCCAddressFromDraft (addressValue) {
  const bccs = getCurrentDraft().bccs.filter((tosAddress) => tosAddress !== addressValue)
  setActiveDraftValue('bccs', bccs)
}

export function storeNewAttachment (attachment) {
  const attachments = [...getCurrentDraft().attachments || [], attachment]
  setActiveDraftValue('attachments', attachments)
  sendAttachmentsUpdatedEvent()
}

export function updateAttachment (filename, attachment) {
  const updatedAttachments = getCurrentDraft().attachments.map((existingAttachment) => {
    if (existingAttachment.filename === filename) { return attachment }
    return existingAttachment
  })
  setActiveDraftValue('attachments', updatedAttachments)
  sendAttachmentsUpdatedEvent()
}

export function getCurrentDraft () {
  return getCurrentDraftFromList()
}

export function setActiveDraftValue (propertyName, value) {
  store.dispatch(
    setValueOfActiveDraft({
      field: propertyName,
      value: value
    })
  )
  sendUpdateDraftEvents()
}

export function deleteAttachmentFromStore (attachmentId) {
  const attachments = getCurrentDraftFromList().attachments.filter((attachment) => attachment.id !== attachmentId)
  setActiveDraftValue('attachments', attachments)
  sendAttachmentsUpdatedEvent()
}

export function getCurrentDraftFromList () {
  const draftId = store.getState().drafts.activeDraftId
  const draft = draftId ? store.getState().drafts.draftListObject[draftId] : undefined
  return draft?.type === getDraftType() ? draft : undefined
}

export function getTemporalDraft () {
  return store.getState().drafts.temporalDraftValues
}

export function setCurrentDraft (draft) {
  store.dispatch(setValuesOfActiveDraftOnList(draft))
}

export function setDraftType (type) {
  store.dispatch(setDraftMessageType(type))
}

export function getDraftType () {
  return store.getState().drafts.type
}

export function getStoredDraftMessage (id) {
  const draftList = Object.values(store.getState().drafts.draftList)
  return draftList.filter((draft) => draft.id === id)[0]
}

function parseDraftFromAPI (draft) {
  return {
    id: draft.messagedraftid,
    updatedAt: draft.updatedAt,
    createdAt: draft.createdAt,
    from: draft.boxname,
    // `|| []` to match ccs/bccs below. Without it a draft whose payload
    // omits tos lands with tos === undefined, which breaks both the
    // duplicate check and the `[...tos]` spread in addToAddressToStore.
    tos: draft.tos || [],
    ccs: draft.ccs || [],
    bccs: draft.bccs || [],
    tosDirty: draft.tosDirty,
    messageHTML: draft.html,
    messageText: draft.plain,
    subject: draft.subject || '',
    slateContent: draft.slatecontent,
    attachments: draft.attachmentdrafts?.map(attachment => ({
      filesize: attachment.filesize,
      filename: attachment.filename,
      id: attachment.composedid,
      messagedraftid: attachment.messagedraftid,
      mimetype: attachment.mimetype,
      uri: attachment.uri
    })) || [],
    type: draft.type || 'standalone',
    active: draft.active || false,
  }
}

export function storeDraftOnListAndMarkAsActive (draft, isNew = true) {
  if (isNew) {
    draft = parseDraftFromAPI(draft)
    const syncedDraft = Object.assign(draft, getTemporalDraft())
    storeDraftsOnList([syncedDraft], false)
  } else {
    storeDraftsOnList([draft])
  }

  markDraftAsActive(draft)
  store.commit('DRAFT_MESSAGE:SWITCHED', draft)
  if (draft.parentMessageId) { store.commit(`DRAFT_MESSAGE_FOR_PARENT:${draft.parentMessageId}:CREATED`, draft) }
}

export function markDraftAsActive (draft) {
  store.dispatch(setActiveDraft(draft?.messagedraftid || draft?.id))
  sendUpdateDraftEvents()
}

export function storeDraftsOnList (draftList, shouldParse = true) {
  const draftListObject = {}
  draftList.forEach((draft) => {
    if (shouldParse) {
      draft = parseDraftFromAPI(draft)
    }
    draftListObject[draft.id] = draft
  })

  store.dispatch(
    addDraftListObject(draftListObject)
  )
  sendAttachmentsUpdatedEvent()
  sendUpdateDraftEvents()

  return draftListObject
}

export function switchDraftMessage (id) {
  store.dispatch(setActiveDraft(id))
  store.commit('DRAFT_MESSAGE:SWITCHED', getCurrentDraft())
}

export function getLastDraftsList () {
  return store.getState().drafts.draftListObject
}

function sendAttachmentsUpdatedEvent () {
  store.commit('DRAFT_MESSAGE:ATTACHMENTS_UPDATED', getCurrentDraft())
}

function sendUpdateDraftEvents () {
  store.commit('DRAFT_MESSAGE:UPDATE', getCurrentDraft())
  store.commit('DRAFTS_LIST:UPDATE')
}
